// lib/continuum/research/solution-chains.js
// ═══════════════════════════════════════════════════════════
// Cross-finding solution-chain analyzer.
//
// Given a set of findings + dynamics metadata, identify chains
// of the form:
//
//   "[Source dynamic] survived Phase A and has prescription X.
//    [Target dynamic] is structurally similar (shared cluster /
//    matching gap profile / shared concepts) but is currently
//    [divergent / pending / unverified]. Try applying X's
//    prescription shape to the target — adapted for [the
//    divergence direction or the pending data state]."
//
// This is the "methodology compounds" mechanism — turning
// surveyed findings into instructions for the next ones.
//
// Similarity scoring (option A — field-shape, no embeddings):
//   +30 if both dynamics share `cluster`
//   +5  per matching gap-severity level (D/M/C/E, max 4)
//   +3  per shared concept token in human_translation
//   max ~80; threshold for surfacing: score >= 18
//
// Pure JS, zero dependencies. Importable from anywhere.
// ═══════════════════════════════════════════════════════════

const STOP = new Set([
  // Generic English
  'the', 'and', 'of', 'to', 'in', 'is', 'a', 'an', 'that', 'this',
  'for', 'on', 'with', 'as', 'by', 'at', 'from', 'it', 'be', 'are',
  'was', 'were', 'has', 'have', 'had', 'or', 'not', 'but', 'their',
  'they', 'these', 'those', 'which', 'when', 'where', 'what', 'who',
  'them', 'than', 'then', 'into', 'about', 'also', 'more', 'most',
  'other', 'such', 'some', 'each', 'both', 'between', 'cross',
  // Domain template words — every Continuum finding has these,
  // so matching on them is meaningless. Excluding so concept
  // overlap reflects real shared mechanism vocabulary, not
  // boilerplate.
  'eco', 'human', 'system', 'systems', 'finding', 'findings',
  'dynamic', 'dynamics', 'across', 'domain', 'domains',
  'parallel', 'divergent', 'verdict', 'confidence', 'ecological',
  'show', 'shows', 'measure', 'measures', 'measurable',
  'p_value', 'effect', 'size', 'significant', 'cohen',
  'higher', 'lower', 'similar', 'distinguishable',
  'note', 'including', 'including', 'rather', 'though',
]);

function extractConcepts(text) {
  if (!text || typeof text !== 'string') return [];
  return [...new Set(
    text.toLowerCase()
      .replace(/[^a-z0-9\s-]/g, ' ')
      .split(/\s+/)
      .filter(w => w.length > 4 && !STOP.has(w))
  )];
}

export function computeStructuralSimilarity(dynA, dynB) {
  if (!dynA || !dynB) return { score: 0, factors: [] };
  if (dynA.key === dynB.key) return { score: 0, factors: [] };  // self-match excluded

  let score = 0;
  const factors = [];

  // 1. Cluster match (heaviest signal)
  if (dynA.cluster && dynA.cluster === dynB.cluster) {
    score += 30;
    factors.push({ kind: 'cluster', label: `Both in ${dynA.cluster.replace(/_/g, ' ')} cluster`, weight: 30 });
  }

  // 2. Gap-profile match (D/M/C/E severity)
  const gapsA = dynA.gaps || {};
  const gapsB = dynB.gaps || {};
  const gapKeys = ['data', 'measurement', 'causation', 'ethics'];
  const gapMatches = [];
  for (const k of gapKeys) {
    if (gapsA[k] && gapsB[k] && gapsA[k] === gapsB[k]) {
      gapMatches.push({ key: k, level: gapsA[k] });
    }
  }
  if (gapMatches.length > 0) {
    score += gapMatches.length * 5;
    factors.push({
      kind: 'gaps',
      label: `${gapMatches.length} of 4 gap-severity levels match (${gapMatches.map(g => `${g.key}=${g.level}`).join(', ')})`,
      weight: gapMatches.length * 5,
    });
  }

  // 3. Concept overlap in human_translation
  const conceptsA = extractConcepts(dynA.human_translation);
  const conceptsB = extractConcepts(dynB.human_translation);
  const shared = conceptsA.filter(c => conceptsB.includes(c));
  if (shared.length > 0) {
    score += shared.length * 3;
    factors.push({
      kind: 'concepts',
      label: `Shared concepts: ${shared.slice(0, 5).join(', ')}${shared.length > 5 ? `, +${shared.length - 5} more` : ''}`,
      weight: shared.length * 3,
    });
  }

  return { score, factors };
}

// ─── Chain finder ─────────────────────────────────────────────
// For each "target" finding (DIVERGENT or pending), find the
// best-matched "source" finding (Phase A survived with a working
// prescription) and emit a chain. Returns an array of chains
// sorted by source defensibility, then target leverage.
//
// minScore filters out matches that are too weak to be
// suggestive — default 18 (cluster match alone, or strong
// concept overlap, or moderate gap + some concept overlap).
export function findSolutionChains({
  comparisons = [],
  dynamics = [],
  minScore = 30,
  perTargetLimit = 1,
}) {
  const dynByKey = Object.fromEntries(dynamics.map(d => [d.key, d]));

  const isSurvived = c => (c.phase_a_status === 'survived' || c.phase_a_status === 'survived-respecified');
  // Targets are findings that aren't yet Phase-A-verified — they're the
  // ones who need to borrow defensibility from a verified source.
  // Phase-A-survived findings (even divergent ones) don't qualify; they
  // already have their own evidence chain.
  const isCandidateTarget = c => {
    if (c.phase_a_status) return false;
    const verdict = (c.verdict || '').toLowerCase();
    if (!verdict) return true;
    if (['pending', 'insufficient_data'].includes(verdict)) return true;
    return ['parallel', 'near-parallel', 'divergent'].includes(verdict);
  };

  const sources = comparisons.filter(c => {
    if (!isSurvived(c)) return false;
    const dyn = dynByKey[c.dynamic_key];
    return !!(dyn?.ecological_prescription || dyn?.human_translation);
  });
  const targets = comparisons.filter(isCandidateTarget);

  if (sources.length === 0 || targets.length === 0) return [];

  const chains = [];
  for (const target of targets) {
    const targetDyn = dynByKey[target.dynamic_key];
    if (!targetDyn) continue;

    const matches = sources
      .map(source => {
        const sourceDyn = dynByKey[source.dynamic_key];
        const sim = computeStructuralSimilarity(sourceDyn, targetDyn);
        return { source, sourceDyn, sim };
      })
      .filter(m => m.sim.score >= minScore)
      .sort((a, b) => b.sim.score - a.sim.score)
      .slice(0, perTargetLimit);

    for (const m of matches) {
      chains.push({
        source: m.source,
        sourceDyn: m.sourceDyn,
        target,
        targetDyn,
        score: m.sim.score,
        factors: m.sim.factors,
        reasoning: composeChainReasoning(m.source, m.sourceDyn, target, targetDyn, m.sim),
      });
    }
  }

  // Sort: stronger matches first; among equals, prefer divergent
  // targets (those have the highest insight payoff if a chain works).
  return chains.sort((a, b) => {
    const targetWeight = c => (String(c.verdict || '').toLowerCase() === 'divergent' ? 1 : 0);
    return (b.score + targetWeight(b.target) * 5) - (a.score + targetWeight(a.target) * 5);
  });
}

function composeChainReasoning(source, sourceDyn, target, targetDyn, sim) {
  const sourcePhase = source.phase_a_status === 'survived-respecified'
    ? 'survived Phase A (respecified)'
    : 'survived Phase A';
  const targetState = (() => {
    const v = String(target.verdict || '').toLowerCase();
    if (v === 'divergent') return `currently divergent (d=${(Number(target.effect_size) || 0).toFixed(2)})`;
    if (v === 'pending' || v === 'insufficient_data' || !target.verdict) return 'awaiting data';
    if (!target.phase_a_status) return 'not yet Phase A reviewed';
    return v;
  })();

  const adaptation = (() => {
    const v = String(target.verdict || '').toLowerCase();
    if (v === 'divergent') {
      const direction = (Number(target.effect_size) || 0) > 0 ? 'eco-side' : 'human-side';
      return `Account for the ${direction}-stronger divergence — the prescription's intervention point may need to be applied with different timing or magnitude on the target side.`;
    }
    if (v === 'pending' || !target.verdict) {
      return `Use the prescription as a starting prior for what to look for once data arrives, rather than as a confirmed cross-domain rule.`;
    }
    if (!target.phase_a_status) {
      return `Treat the chain as suggestive only until Phase A on the target distinguishes mechanism transfer from operationalization coincidence.`;
    }
    return `Apply with the standard caveats about sample size and dataset coverage.`;
  })();

  return {
    sourcePhase,
    targetState,
    adaptation,
    summary: `${sourceDyn.label} ${sourcePhase}; ${targetDyn.label} is ${targetState}. Structural similarity: ${sim.factors.map(f => f.label.toLowerCase()).join('; ')}.`,
  };
}

function isSubstantive(c) {
  return c.verdict && !['pending', 'insufficient_data', 'error'].includes(String(c.verdict).toLowerCase());
}
